import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import ModalBody from '../../Modal/ModalBody';
import Button from '../../Button/Button';
import SectionLoader from '../../Loader/SectionLoader';
import ProductImage from '../../LegacyOrder/ProductImage';
import { ReactComponent as AmberAlertTriangle } from '../../../assets/images/svg/amber-alert-triangle.svg';
import SellFromHomeStatusTimeline from './SellFromHomeStatusTimeline';
import SellFromHomeCancelOrderDrawer from './SellFromHomeCancelOrderDrawer';
import SellFromHomeOrderCancelledDrawer from './SellFromHomeOrderCancelledDrawer';
import {
  NOT_AVAILABLE,
  FIELD_CAPTION_TYPE,
  getTransactionField,
  buildTimelineSteps,
} from './sellFromHomeTransactionHelpers';
import { getCurrentAccountUid } from '../../../store/selectors/accountSelectors';
import { selectPortfolioSingleTransaction } from '../../../store/selectors/portfolioTransactionsSelector';
import {
  selectSellFromHomeHistory,
  selectSellFromHomeHistoryError,
} from '../../../store/selectors/sellFromHomeHistorySelectors';
import { selectIsLoadingByActionTypes } from '../../../store/selectors/loadingSelectors';
import {
  portfolioTransactionsSingleFetch,
  emptyPortfolioTransactionSingle,
} from '../../../store/actions/portfolio/portfolioActions';
import {
  getSellFromHomeHistory,
  emptySellFromHomeHistory,
} from '../../../store/actions/orders/orderActions';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import { GET_SELL_FROM_HOME_HISTORY_LOADING } from '../../../store/actions/orders/orderActionConstants';
import {
  formatDate,
  formatSfhTimelineDate,
} from '../../../util/helpers/dateHelpers';
import { formatMoneyNumeral } from '../../../util/helpers/numeralHelpers';
import { pxToRem } from '../../../assets/styles/helper';
import themeColors from '../../../assets/styles/themeColors';
import { variables } from '../../../assets/styles/variables';

const SkuSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${pxToRem(12)};
  flex-shrink: 0;
  padding: ${pxToRem(8)} 0 ${pxToRem(16)};
  margin: 0 ${pxToRem(-16)};
  padding-left: ${pxToRem(16)};
  padding-right: ${pxToRem(16)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
`;

const SkuImage = styled.div`
  flex-shrink: 0;

  > img {
    width: ${pxToRem(56)};
    height: ${pxToRem(56)};
    object-fit: contain;
    margin: 0;
  }
`;

const SkuText = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${pxToRem(2)};
  min-width: 0;
`;

const SkuName = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(18)};
  font-weight: 600;
  color: ${themeColors.colorTextPrimary};
`;

const SkuSub = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  color: ${themeColors.colorTextSecondary};
`;

const TimelineTitle = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(16)};
  color: ${themeColors.colorTextSecondary};
  flex-shrink: 0;
  padding: ${pxToRem(16)} 0 ${pxToRem(12)};
`;

const TimelineScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

const Footer = styled.div`
  > button {
    width: 100%;
  }
`;

const FetchErrorText = styled.div`
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  color: ${themeColors.colorError};
  text-align: center;
  padding: ${pxToRem(24)} 0;
`;

const TimelineNotice = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${pxToRem(8)};
  background: ${themeColors.colorBackgroundSecondary};
  border-radius: ${pxToRem(4)};
  padding: ${pxToRem(12)};
  margin-top: ${pxToRem(16)};
  font-family: ${variables.family.goldSilver}, sans-serif;
  font-size: ${pxToRem(14)};
  font-weight: 500;
  line-height: 140%;
  color: ${themeColors.colorTextSecondary};

  > svg {
    width: ${pxToRem(20)};
    height: ${pxToRem(20)};
    flex-shrink: 0;
    margin-top: ${pxToRem(2)};
  }
`;

const SellFromHomeTransactionHistory = ({
  selectedHit,
  quantity,
  lockedSnapshot,
  handleClose,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const accountUid = useSelector(getCurrentAccountUid);
  const singleTransaction = useSelector(selectPortfolioSingleTransaction);
  const history = useSelector(selectSellFromHomeHistory);
  const historyError = useSelector(selectSellFromHomeHistoryError);
  const isFetching = useSelector(
    selectIsLoadingByActionTypes([
      PORTFOLIO_TRANSACTIONS_SINGLE_LOADING,
      GET_SELL_FROM_HOME_HISTORY_LOADING,
    ]),
  );
  const [isCancelOpen, setIsCancelOpen] = useState(false);
  const [isCancelledOpen, setIsCancelledOpen] = useState(false);
  const [isCancelled, setIsCancelled] = useState(false);
  const [cancelledAt, setCancelledAt] = useState(null);

  const transactionUid = lockedSnapshot?.transactionUid;

  useEffect(() => {
    if (!accountUid || !transactionUid) return undefined;
    dispatch(portfolioTransactionsSingleFetch({ accountUid, transactionUid }));
    return () => {
      dispatch(emptyPortfolioTransactionSingle());
    };
  }, [accountUid, transactionUid, dispatch]);

  const fields = singleTransaction?.Fields ?? [];

  // The view-history endpoint is keyed by the SFH order code, which is the
  // reference number shown on the details screen (already in "SFH-..." form).
  // It's carried in the fetched transaction fields, so the history fetch is
  // chained off the single-transaction fetch above.
  const referenceField = getTransactionField(
    fields,
    FIELD_CAPTION_TYPE.REFERENCE_NUMBER,
  );
  const orderCode = referenceField?.Caption?.trim() || null;

  useEffect(() => {
    if (!orderCode) return undefined;
    dispatch(getSellFromHomeHistory({ orderCode }));
    return () => {
      dispatch(emptySellFromHomeHistory());
    };
  }, [orderCode, dispatch]);

  // When launched from the wizard we have the selected product (selectedHit);
  // when launched from Recent Transactions we only have a Uid, so fall back to
  // the product details carried in the fetched transaction fields.
  const productImageField = fields.find((f) => f.FormatType === 'ProductImage');
  const productField = fields.find((f) => f.FormatType === 'Product');
  const summaryField = fields.find((f) => f.CaptionType === 'Summary');

  const productCode = selectedHit?.Code ?? productImageField?.ProductCode;
  const productCaption = selectedHit
    ? `${selectedHit?.WeightCaption ?? ''} ${selectedHit?.Caption ?? ''}`.trim()
    : productField?.ProductCaption ?? '';
  const displayQuantity = quantity || summaryField?.AmountOrQuantity;
  // Show the backend timestamp exactly as received (no timezone conversion);
  // only fall back to the client submission time if the BE value is missing.
  const submittedAt =
    singleTransaction?.DateTime || lockedSnapshot?.submittedAt;

  const cancelSubtitle = `${displayQuantity} × ${productCaption} · ${
    lockedSnapshot?.payout != null
      ? formatMoneyNumeral(lockedSnapshot.payout)
      : NOT_AVAILABLE
  }`;

  const cancelledNotice = (
    <TimelineNotice>
      <AmberAlertTriangle />
      <span>
        {t('sellWizard.sfhWizard.transactionHistory.marketLossNotice')}
      </span>
    </TimelineNotice>
  );

  // When cancelled, the timeline ends on the terminal Cancelled status with the
  // cancellation time; the market-loss notice is rendered full-width below the
  // timeline. Otherwise show the progress sequence with the current status.
  const steps = isCancelled
    ? [
        {
          key: 'requestSubmitted',
          label: t(
            'sellWizard.sfhWizard.transactionHistory.status_requestSubmitted',
          ),
          completed: true,
          subText: submittedAt ? formatSfhTimelineDate(submittedAt) : null,
        },
        {
          key: 'cancelled',
          label: t('sellWizard.sfhWizard.transactionHistory.status_cancelled'),
          cancelled: true,
          subText: cancelledAt ? formatSfhTimelineDate(cancelledAt) : null,
        },
      ]
    : buildTimelineSteps(history).map((step, idx) => {
        const completed = step.status === 'completed';
        const isCancelledStep = step.status === 'cancelled';
        let subText = null;
        if (step.dateTime) {
          subText = formatSfhTimelineDate(step.dateTime);
        } else if (idx === 0 && completed && submittedAt) {
          // Fall back to the client submission time if the BE omits the
          // timestamp on the first (Request Submitted) step.
          subText = formatSfhTimelineDate(submittedAt);
        } else if (!completed && !isCancelledStep) {
          subText = t('sellWizard.sfhWizard.transactionHistory.pending');
        }
        return {
          key: step.key,
          label: t(
            `sellWizard.sfhWizard.transactionHistory.status_${step.key}`,
          ),
          completed,
          cancelled: isCancelledStep,
          subText,
          // Cancel Order is only available while the BE still flags the step
          // as cancellable (the first step before it leaves Request Submitted).
          showCancel: Boolean(step.cancellable),
        };
      });

  const handleCancelOrder = () => setIsCancelOpen(true);

  // TODO(BE): call the real cancel endpoint with the chosen reason.
  const handleCancelConfirmed = () => {
    setIsCancelOpen(false);
    setIsCancelled(true);
    // Client-side "now" (BE doesn't return a cancellation timestamp yet); a
    // Date object is formatted in local time by formatSfhTimelineDate.
    setCancelledAt(new Date());
    setIsCancelledOpen(true);
  };

  const handleCancelledOk = () => setIsCancelledOpen(false);

  // Keep the loader up until the history feed lands (it's chained off the
  // single-transaction fetch) so the timeline never flashes its all-pending
  // loading fallback before the real status arrives. Once we have an orderCode
  // a history fetch is guaranteed to follow, so bridge that gap too; release on
  // error so the spinner can't get stuck.
  const hasHistory = Array.isArray(history) && history.length > 0;
  const showLoader =
    !hasHistory && !historyError && (isFetching || Boolean(orderCode));

  return (
    <SectionLoader isLoading={showLoader}>
      <ModalBody>
        <SkuSection>
          <SkuImage>
            <ProductImage productCode={productCode} width={56} height={56} />
          </SkuImage>
          <SkuText>
            <SkuName>
              {t('sellWizard.sfhWizard.transactionHistory.productLine', {
                qty: displayQuantity,
                caption: productCaption,
              })}
            </SkuName>
            <SkuSub>
              {t('sellWizard.sfhWizard.transactionHistory.soldFrom', {
                date: submittedAt ? formatDate(submittedAt) : '',
              })}
            </SkuSub>
          </SkuText>
        </SkuSection>

        <TimelineTitle>
          {t('sellWizard.sfhWizard.transactionHistory.timeline')}
        </TimelineTitle>

        <TimelineScroll>
          {historyError && !hasHistory && !isCancelled ? (
            <FetchErrorText data-cy="text-sfh-history-fetch-error">
              {t('sellWizard.sfhWizard.transactionHistory.fetchFailed')}
            </FetchErrorText>
          ) : (
            <>
              <SellFromHomeStatusTimeline
                steps={steps}
                cancelLabel={t(
                  'sellWizard.sfhWizard.transactionHistory.cancelOrder',
                )}
                onCancelOrder={handleCancelOrder}
              />
              {isCancelled && cancelledNotice}
            </>
          )}
        </TimelineScroll>

        <Footer>
          <Button
            variant="primaryOutlined"
            textTransform="none"
            onClick={handleClose}
            data-cy="button-sfh-history-close"
          >
            {t('sellWizard.sfhWizard.transactionHistory.close')}
          </Button>
        </Footer>
      </ModalBody>

      <SellFromHomeCancelOrderDrawer
        isOpen={isCancelOpen}
        onClose={() => setIsCancelOpen(false)}
        onConfirm={handleCancelConfirmed}
        subtitle={cancelSubtitle}
      />

      <SellFromHomeOrderCancelledDrawer
        isOpen={isCancelledOpen}
        onClose={handleCancelledOk}
        subtitle={cancelSubtitle}
      />
    </SectionLoader>
  );
};

SellFromHomeTransactionHistory.propTypes = {
  selectedHit: PropTypes.shape({
    Code: PropTypes.string,
    Caption: PropTypes.string,
    WeightCaption: PropTypes.string,
  }),
  quantity: PropTypes.number,
  lockedSnapshot: PropTypes.shape({
    transactionUid: PropTypes.string,
    submittedAt: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.instanceOf(Date),
      PropTypes.number,
    ]),
    payout: PropTypes.number,
  }),
  handleClose: PropTypes.func,
};

export default SellFromHomeTransactionHistory;
